import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppThunkDispatch } from 'state/store';
import {
  fetchLearningTeam,
  selectLearningTeamName,
  selectIsLearningTeamLoading,
  selectLearningTeam,
  selectUpdateLearningTeamNotification,
  clearUpdateLearningTeamNotification,
} from 'state/LearningTeam/learningTeamSlice';
import useSnackbarNotification from 'Hooks/useSnackbarNotification';

export const useLearningTeam = () => {
  const dispatch = useAppThunkDispatch();
  const { teamId } = useParams<{
    teamId: string;
  }>();
  const learningTeamName = useSelector(selectLearningTeamName);
  const isLearningTeamLoading = useSelector(selectIsLearningTeamLoading);
  const learningTeam = useSelector(selectLearningTeam);
  const updateLearningTeamNotification = useSelector(
    selectUpdateLearningTeamNotification
  );
  const {
    showNotification,
    hideNotification,
    snackbarProps,
    isNotificationVisible,
  } = useSnackbarNotification();

  const handleClearUpdateLearningTeamNotification = useCallback(() => {
    hideNotification();
    dispatch(clearUpdateLearningTeamNotification());
  }, [dispatch, hideNotification]);

  useEffect(() => {
    dispatch(fetchLearningTeam({ id: teamId }));
  }, [dispatch, teamId]);

  useEffect(() => {
    if (updateLearningTeamNotification) {
      showNotification(updateLearningTeamNotification);
    }
  }, [
    updateLearningTeamNotification,
    showNotification,
    dispatch,
    handleClearUpdateLearningTeamNotification,
  ]);

  const notificationProps = useMemo(() => {
    return {
      ...snackbarProps,
      onCloseCallback: handleClearUpdateLearningTeamNotification,
    };
  }, [snackbarProps, handleClearUpdateLearningTeamNotification]);

  useEffect(() => {
    return () => handleClearUpdateLearningTeamNotification();
  }, [handleClearUpdateLearningTeamNotification]);

  return useMemo(() => {
    return {
      learningTeamName,
      isLearningTeamLoading,
      learningTeam,
      notificationProps,
      isNotificationVisible,
    };
  }, [
    learningTeamName,
    isLearningTeamLoading,
    learningTeam,
    notificationProps,
    isNotificationVisible,
  ]);
};
